import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import { useAppSelector, useAppDispatch } from "hooks/reduxHooks";
import { Button } from "components/Buttons";
import { ButtonColorScheme, ButtonType } from "types/ui";
import { useHistory } from "react-router-dom";
import { getCoachTeams } from "repositories/teamsApi";

import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import Tab from "@mui/material/Tab";
import Pagination from "../../../components/Pagination/Pagination";
import { PatientRegion } from "types/PatientRegion";
import { AthleteTableHeader } from "../components/Athlete/AthleteTableHeader";
import { AthleteTableRow } from "../components/Athlete/AthleteTableRow";
import { Loading } from "../../../components/Loading/Loading";
import { styled } from "@mui/material";
import { getCoachFreeformAthletes } from "repositories/coachApi";
import { enqueueSnackbar } from "notistack";
import { SearchBoxBase } from "../../../components/SearchBox/SearchBox";
import { initPatientRegionsPage } from "store/PatientRegionsStore/actions";

type Props = {
  patientRegionsState: PatientRegion[];
  onClick?: any;
  coachId: string;
  sortConfig: any;
  selectedTab: any;
  setSelectedTab: any;
  loading: boolean;
  onPageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  page: number;
  totalPages: number;
};

const StyledTab = styled(Tab)({
  "&.MuiButtonBase-root": {
    backgroundColor: "#F0F0F0",
    textTransform: "none",
    borderRadius: 10,
    fontSize: 14,
    margin: 10,
    padding: 10,
    fontWeight: "400",
  },
  "&.Mui-selected": {
    color: "white",
    backgroundColor: "#161728",
    textTransform: "none",
    borderRadius: 10,
    fontSize: 14,
    padding: 10,
    margin: 10,
    fontWeight: "400",
  },
});

export const ContainerForCoaches = ({
  patientRegionsState,
  onClick,
  sortConfig,
  loading,
  coachId,
  selectedTab,
  setSelectedTab,
  ...props
}: Props) => {
  const currentClinician = useAppSelector(selectCurrentClinician);
  const [freeFormAthletes, setFreeFormAthletes] = useState([]);
  const [freeFormAthletesLoading, setFreeFormAthletesLoading] = useState(false);
  const [searchValue, setSeachValue] = useState("");
  const [updateSearch, setUpdateSearch] = useState(false);
  const [sortDirection, setSortDirection] = useState("asc");
  const [teamList, setTeamList] = useState([]);
  const history = useHistory();
  const dispatch = useAppDispatch();
  const getTeams = async () => {
    if (!currentClinician) return;
    try {
      const fetchedTeams = await getCoachTeams(currentClinician._id);
      setTeamList(fetchedTeams);
    } catch (error) {
      setTeamList([]);
    }
  };

  useEffect(() => {
    getTeams();
  }, []);

  useEffect(() => {
    dispatch(initPatientRegionsPage());
  }, [selectedTab]);

  const renderAthletes = (
    <>
      {patientRegionsState.map((patientRegion) => {
        return (
          <AthleteTableRow
            gender={patientRegion.patient?.profileInfo?.gender}
            teamList={teamList}
            key={patientRegion._id}
            user_id={patientRegion._id}
            currentAverage={patientRegion?.calculations?.currentAverage}
            leftSD={{
              value: patientRegion?.leftSideData?.current?.leftMinValue,
              isBetter:
                (patientRegion?.leftSideData?.current?.leftMinValue ?? 0) >
                (patientRegion?.leftSideData?.previous?.leftMinValue ?? 0),
            }}
            rightSD={{
              value: patientRegion?.rightSideData?.current?.rightMinValue,
              isBetter:
                (patientRegion?.rightSideData?.current?.rightMinValue ?? 0) >
                (patientRegion?.rightSideData?.previous?.rightMinValue ?? 0),
            }}
            bothSD={[
              {
                leftValue: patientRegion?.bothSideData?.current?.leftMinValue,
                leftIsBetter:
                  (patientRegion?.bothSideData?.current?.leftMinValue ?? 0) >
                  (patientRegion?.bothSideData?.previous?.leftMinValue ?? 0),
              },
              {
                rightValue: patientRegion?.bothSideData?.current?.rightMinValue,
                rightIsBetter:
                  (patientRegion?.bothSideData?.current?.rightMinValue ?? 0) >
                  (patientRegion?.bothSideData?.previous?.rightMinValue ?? 0),
              },
            ]}
            patientImage={patientRegion.patient?.imageUrl}
            firstName={patientRegion.patient.profileInfo.firstName}
            lastName={patientRegion.patient.profileInfo.lastName}
            inviteDates={patientRegion.patient.profileInfo.inviteDates}
            dateOfBirth={patientRegion.patient.profileInfo.dateOfBirth}
            email={patientRegion.patient.profileInfo.email}
            status={patientRegion.patient?.status}
            authStatus={patientRegion.patient.profileInfo.authStatus}
            lastExam={patientRegion.lastExam}
            userSports={patientRegion.patient?.profileInfo?.sports ?? []}
            teams={patientRegion.patient?.profileInfo?.teams ?? []}
            position={patientRegion.patient?.profileInfo?.sportPosition ?? []}
          />
        );
      })}
    </>
  );
  const renderFreeFormAthletes = (
    <>
      {freeFormAthletes.map((freeFromAthlete: any) => {
        return (
          <AthleteTableRow
            gender={freeFromAthlete.profileInfo?.gender}
            teamList={teamList}
            isFreeForm={true}
            key={freeFromAthlete._id}
            user_id={freeFromAthlete._id}
            patientImage={freeFromAthlete?.imageUrl}
            firstName={freeFromAthlete.profileInfo.firstName}
            lastName={freeFromAthlete.profileInfo.lastName}
            inviteDates={freeFromAthlete.profileInfo.inviteDates}
            dateOfBirth={freeFromAthlete.profileInfo.dateOfBirth}
            email={freeFromAthlete.profileInfo.email}
            status={freeFromAthlete?.status}
            authStatus={freeFromAthlete.profileInfo.authStatus}
            lastExam={freeFromAthlete.lastExam?.createdAt}
            userSports={freeFromAthlete.profileInfo?.sports ?? []}
            teams={freeFromAthlete.profileInfo?.teams ?? []}
            position={freeFromAthlete.profileInfo?.sportPosition ?? []}
          />
        );
      })}
    </>
  );

  const getFreeformAthletes = async (searchValue?: string, sort?: string) => {
    try {
      setFreeFormAthletesLoading(true);
      const athletes = await getCoachFreeformAthletes(
        coachId,
        searchValue,
        sort
      );
      setFreeFormAthletes(athletes);
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
    setFreeFormAthletesLoading(false);
  };
  useEffect(() => {
    if (selectedTab === 1) {
      getFreeformAthletes(searchValue);
    }
  }, [selectedTab, updateSearch]);

  const freeFormSort = (field: any) => {
    let direction = sortDirection;
    if (sortDirection === "asc") {
      direction = "desc";
    } else {
      direction = "asc";
    }
    setSortDirection(direction);
    getFreeformAthletes(searchValue, `${field}:${direction}`);
  };
  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Tabs
          value={selectedTab}
          onChange={(e, v) => {
            setSelectedTab(v);
          }}
          TabIndicatorProps={{
            style: { backgroundColor: "transparent" },
          }}
        >
          <StyledTab disabled={false} label="Athletes" />
          {currentClinician?.freeFormEnabled && (
            <StyledTab disabled={false} label="Freeform athletes" />
          )}

          <Box flex={1} />
        </Tabs>

        {selectedTab === 1 && (
          <div style={{ position: "relative", left: "150px" }}>
            <SearchBoxBase
              value={searchValue}
              onChange={(e: any) => {
                setSeachValue(e.target.value);
              }}
              onKeyDown={(e: any) => {
                if (e.key === "Enter") {
                  setUpdateSearch(!updateSearch);
                }
              }}
              loading={freeFormAthletesLoading}
              onClear={() => {
                setSeachValue("");
                setUpdateSearch(!updateSearch);
              }}
              onBlur={() => {
                setUpdateSearch(!updateSearch);
              }}
            />
          </div>
        )}
        <Button
          style={{ maxHeight: "40px" }}
          title={"Invite athlete"}
          onClick={() => {
            history.push("/invite-athlete");
          }}
          type={ButtonType.normal}
          colorScheme={ButtonColorScheme.gradient}
        />
      </Box>

      <AthleteTableHeader
        isFreeForm={selectedTab === 1}
        onClick={selectedTab === 0 ? onClick : freeFormSort}
        sortConfig={sortConfig}
      />
      <div className="w-full min-h-[200px] flex flex-col overflow-y-hidden space-y-[5px] relative">
        {selectedTab == 0 && renderAthletes}
        {selectedTab == 1 && renderFreeFormAthletes}
        <Loading
          loading={loading || freeFormAthletesLoading}
          states={patientRegionsState}
        />
      </div>
      {selectedTab == 0 && (
        <Pagination
          count={props.totalPages}
          page={props.page}
          disabled={loading}
          onChange={props.onPageChange}
        />
      )}
    </>
  );
};
